// The Responses tab's download: every response the workflow holds, as one
// JSON document.
//
// The document is built here, apart from the button that asks for it, so the
// shape is a pure function of a workflow, its versions and its captures and
// can be pinned by a test without a browser. What is in it is what the
// Responses tab shows and no more: the workflow's identity, the current
// draft, every published version the responses name, then each capture's own
// fields — `values` untouched, one object per step keyed by the step's name,
// exactly as it was stored, and the `version` that says which declaration in
// `versions` they answer. Nothing about who owns the workflow, and nothing
// about the session that asked.
import * as CaptureQL from "queries/capture";

// A file name that says which workflow it is and does not fight the shell:
// the id already passes /Name/Required, so it is safe as it is.
export const filename = (workflow) => `${workflow.workflowId}-responses.json`;

// One capture, as it goes into the file. Apollo's `id` and `__typename` are the
// cache's, not the response's, and are left out; the rest is the row.
const response = (capture) => ({
  captureId: capture.captureId,
  status: capture.status,
  createdAt: capture.createdAt,
  updatedAt: capture.updatedAt,
  // Which entry of `versions` the values answer; null for a response from
  // before versions existed, which is read against `definition`.
  version: capture.version || null,
  definitionUpdatedAt: capture.definitionUpdatedAt || null,
  values: capture.values || {},
});

// One published version, as it goes into the file: the number, when, and the
// declaration — the cache's fields left out as they are on a response.
const published = (version) => ({
  version: version.version,
  publishedAt: version.publishedAt || null,
  title: version.title || "",
  description: version.description || "",
  steps: version.steps || [],
  subforms: version.subforms || [],
});

export const document = (workflow, captures, { exportedAt = new Date(), versions = [] } = {}) => ({
  workflowId: workflow.workflowId,
  title: workflow.title || "",
  description: workflow.description || "",
  exportedAt: exportedAt.toISOString(),
  publishedVersion: workflow.publishedVersion || 0,
  // The DRAFT, as declared now. What a response with no `version` is read
  // against; every other response names its declaration in `versions`.
  definition: {
    updatedAt: workflow.updatedAt || null,
    steps: workflow.steps || [],
    subforms: workflow.subforms || [],
  },
  // Every published version, oldest first, so the file reads in the order
  // the workflow was published.
  versions: [...versions].sort((a, b) => a.version - b.version).map(published),
  count: captures.length,
  responses: captures.map(response),
});

// Every page of the workflow's captures, newest first, walked to the end.
//
// Through the client directly rather than a hook: this runs on a click, reads
// as many pages as there are, and must not leave any of them in the cache as
// "the list" — `network-only` reads fresh and still writes, so the tab's own
// query is `cache-and-network` and reconciles on its next render either way.
// The page size is the API's ceiling.
export const fetchAll = async (client, workflowId) => {
  const captures = [];
  let last = null;

  do {
    const { data } = await client.query({
      query: CaptureQL.list,
      variables: { workflowId, input: { last, limit: 100 } },
      fetchPolicy: "network-only",
    });
    const page = data.viewer.account.workflows.get.captures.list;
    captures.push(...page.items);
    last = page.last || null;
  } while (last);

  return captures;
};

// Hand the browser a file. An object URL on an anchor, clicked and revoked,
// which is the one way a page can save a file it built without a server
// round trip; the anchor is never attached, so nothing of it shows.
export const download = (name, text) => {
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
};
